import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  ListNumbers, UsersFour, TennisBall, Trophy, ArrowsClockwise, ArrowLeft, Bell,
} from '@phosphor-icons/react';
import { findEventBySlug, EVENT_STATUS_LABEL } from '../lib/eventsCatalog';

const ICONS = {
  ListNumbers,
  UsersFour,
  TennisBall,
  Trophy,
  ArrowsClockwise,
};

// Generic event detail shell. Active events whose `route` points elsewhere
// (e.g. Men's Singles Ladder → `/`) redirect immediately. Coming-soon
// events render a placeholder with no destructive controls.
export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const event = findEventBySlug(slug);

  // Unknown slug → bounce to the catalog. Avoids a dead-end 404 page.
  if (!event) {
    return <Navigate to="/events" replace />;
  }

  // Active events whose canonical surface lives elsewhere (e.g. Singles
  // Ladder still lives at `/`) redirect immediately so we don't ship two
  // competing surfaces for the same data.
  if (event.status === 'active' && event.route && event.route !== `/events/${event.slug}`) {
    return <Navigate to={event.route} replace />;
  }

  const Icon = ICONS[event.icon] || ListNumbers;
  const isActive = event.status === 'active';

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      <button
        type="button"
        onClick={() => navigate('/events')}
        className="flex items-center gap-1.5 text-sm text-[#6a766e] hover:text-[#013523] transition-colors"
        data-testid="event-detail-back"
      >
        <ArrowLeft size={16} weight="bold" /> All events
      </button>

      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div
            className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
              isActive
                ? 'bg-[#015e3f]/10 text-[#015e3f]'
                : 'bg-[#e3dcd1]/60 text-[#9aa39d]'
            }`}
            aria-hidden="true"
          >
            <Icon size={26} weight={isActive ? 'fill' : 'regular'} />
          </div>
          <div className="flex-1 min-w-0">
            <h1
              className="font-serif text-3xl text-[#013523] tracking-tight"
              data-testid="event-detail-name"
            >
              {event.name}
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                data-testid="event-detail-status"
                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  isActive
                    ? 'bg-[#015e3f]/10 text-[#015e3f]'
                    : 'bg-[#e3dcd1]/60 text-[#6a766e]'
                }`}
              >
                {EVENT_STATUS_LABEL[event.status] || event.status}
              </span>
              {event.gender && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#f0ece3] text-[#6a766e]">
                  {event.gender}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Coming-soon panel — the only state this page renders today since
          all active events redirect away. */}
      <div
        className="bg-white border border-[#e3dcd1] rounded-2xl p-6 text-center space-y-3"
        data-testid="event-detail-coming-soon-panel"
      >
        <p className="text-sm text-[#2a332c]">{event.blurb}</p>
        <p className="text-xs text-[#6a766e]">
          We&apos;re building this event. Check back soon.
        </p>
        <button
          type="button"
          disabled
          aria-disabled="true"
          data-testid="event-detail-notify-button"
          className="inline-flex items-center gap-2 bg-[#015e3f]/30 text-white rounded-full px-5 py-2 text-sm font-medium cursor-not-allowed"
          title="Notifications open in a later phase."
        >
          <Bell size={14} weight="bold" /> Notify me
        </button>
      </div>

      {/* Generic placeholder sections — what the eventual real page surfaces.
          Rendering them today (greyed out) anchors the visual contract so
          future phases drop content in without restructuring the page. */}
      <div className="grid gap-3 md:grid-cols-2">
        {['About', 'Format & rules', 'Standings', 'Participants', 'History'].map((label) => (
          <div
            key={label}
            className="bg-[#fbfaf8] border border-dashed border-[#e3dcd1] rounded-2xl p-5"
            data-testid={`event-detail-section-${label.toLowerCase().replace(/ /g, '-')}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-[#6a766e]">{label}</div>
            <div className="text-sm text-[#9aa39d] mt-2 italic">
              Coming with this event.
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
